import { useState, useMemo } from 'react'
import { Link, useRouter, useCanGoBack } from '@tanstack/react-router'
import {
  useTimesheetDetail,
  useApproveTimesheet,
  useRejectTimesheet,
} from '@/common/hooks/useApprovals'
import type { Timesheet, TimeEntry, AdjacentContextEntry } from '@/common/hooks/useTimesheets'
import { isOffDay } from '@/common/lib/workSchedule'
import { Button } from '@/common/components/button'
import { AdminOverrideBadge } from '@/common/components/admin-override-badge/AdminOverrideBadge'
import * as tsStyles from '@/common/components/timesheet-page/TimesheetPage.css'
import * as styles from './TimesheetReviewPage.css'
import { colors } from '@/core/styles/tokens'

// ---------------------------------------------------------------------------
// Helpers (shared with TimesheetPage)
// ---------------------------------------------------------------------------

function formatDateShort(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })
}

function formatPeriod(start: string, end: string) {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`
}

function formatPeriodShort(start: string, end: string) {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`
}


function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function computeHoursWorked(
  clockIn1: string | null,
  clockOut1: string | null,
  clockIn2: string | null,
  clockOut2: string | null,
): number {
  let total = 0
  for (const [cin, cout] of [
    [clockIn1, clockOut1],
    [clockIn2, clockOut2],
  ] as const) {
    if (cin && cout) {
      const [h1, m1] = cin.split(':').map(Number)
      const [h2, m2] = cout.split(':').map(Number)
      const diff = h2 * 60 + m2 - (h1 * 60 + m1)
      if (diff > 0) total += diff
    }
  }
  return Math.round((total / 60) * 100) / 100
}

function formatTime(val: string | null): string {
  if (!val) return ''
  const [hStr, mStr] = val.slice(0, 5).split(':')
  let h = Number(hStr)
  const suffix = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${mStr} ${suffix}`
}

function getWeekSunday(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() - d.getDay())
  return d.toISOString().slice(0, 10)
}

function statusStyle(status: string) {
  switch (status) {
    case 'submitted': return styles.statusSubmitted
    case 'approved': return styles.statusApproved
    case 'rejected': return styles.statusRejected
    default: return styles.statusDraft
  }
}

// ---------------------------------------------------------------------------
// Grid types
// ---------------------------------------------------------------------------

interface GridRow {
  date: string
  type: 'current' | 'adjacent'
  entry?: TimeEntry
  adjacentEntry?: AdjacentContextEntry
}

interface WeekSubtotal {
  weekSunday: string
  totalHours: number
  standardHours: number
  flsaOvertimeHours: number
  straightOvertimeHours: number
  isIncomplete: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TimesheetReviewPageProps {
  timesheetId: number
}

/**
 * Back navigation for the read-only review. This page is reached from several
 * places (payroll overview, a user profile, team timesheets), so go back to
 * wherever the user came from. Falls back to Team Timesheets when there is no
 * in-app history (e.g. the URL was opened directly).
 */
function BackLink() {
  const router = useRouter()
  const canGoBack = useCanGoBack()

  if (canGoBack) {
    return (
      <button
        type="button"
        onClick={() => router.history.back()}
        className={styles.backLink}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
      >
        &larr; Back
      </button>
    )
  }
  return (
    <Link to="/manager/timesheets" className={styles.backLink}>
      &larr; Back to Team Timesheets
    </Link>
  )
}

export function TimesheetReviewPage({ timesheetId }: TimesheetReviewPageProps) {
  const { data: timesheet, isLoading, error } = useTimesheetDetail(timesheetId)
  const approveTimesheet = useApproveTimesheet(timesheetId)
  const rejectTimesheet = useRejectTimesheet(timesheetId)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  if (isLoading) {
    return (
      <div className={styles.page}>
        <BackLink />
        <div>Loading timesheet...</div>
      </div>
    )
  }

  if (error || !timesheet) {
    return (
      <div className={styles.page}>
        <BackLink />
        <div className={styles.errorMessage}>
          {error ? `Failed to load timesheet: ${error.message}` : 'Timesheet not found.'}
        </div>
      </div>
    )
  }

  const isSubmitted = timesheet.status === 'submitted'

  const handleApprove = async () => {
    await approveTimesheet.mutateAsync()
  }

  const handleRejectClick = () => {
    setRejectReason('')
    setShowRejectDialog(true)
  }

  const handleConfirmReject = async () => {
    await rejectTimesheet.mutateAsync(rejectReason)
    setShowRejectDialog(false)
  }

  return (
    <div className={styles.page}>
      <BackLink />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Review timesheet</h1>
          <div className={styles.subtitle}>
            {formatPeriod(timesheet.pay_period.start_date, timesheet.pay_period.end_date)}
          </div>
        </div>
        <div className={styles.actions}>
          <span className={statusStyle(timesheet.status)}>{timesheet.status}</span>
          {timesheet.status === 'approved' &&
            (timesheet.is_admin_override ? (
              <AdminOverrideBadge
                approverName={timesheet.approved_by_name}
                overrideAt={timesheet.override_at}
              />
            ) : (
              timesheet.approved_by_name && (
                <span className={styles.approverNote}>Approved by {timesheet.approved_by_name}</span>
              )
            ))}
          {isSubmitted && (
            <>
              <Button
                variant="danger"
                size="sm"
                onClick={handleRejectClick}
                disabled={rejectTimesheet.isPending}
              >
                Reject
              </Button>
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={approveTimesheet.isPending}
              >
                {approveTimesheet.isPending ? 'Approving...' : 'Approve'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Status messages */}
      {approveTimesheet.isSuccess && (
        <div className={styles.successMessage}>Timesheet approved successfully.</div>
      )}
      {approveTimesheet.isError && (
        <div className={styles.errorMessage}>Failed to approve: {approveTimesheet.error.message}</div>
      )}
      {rejectTimesheet.isSuccess && (
        <div className={styles.successMessage}>Timesheet rejected.</div>
      )}

      {/* OT Legend */}
      <div className={tsStyles.otLegend}>
        <span className={tsStyles.otLegendItem}>
          <span className={tsStyles.otDotFlsa} /> OT (1.5x)
        </span>
        <span className={tsStyles.otLegendItem}>
          <span className={tsStyles.otDotStraight} /> Straight OT (1.0x)
        </span>
        <span className={tsStyles.otLegendItem}>
          <span className={tsStyles.otDotPending} /> OT pending
        </span>
      </div>

      {/* Full timesheet grid with weekly subtotals and OT */}
      <TimesheetGrid timesheet={timesheet} />

      {/* Reject confirmation dialog */}
      {showRejectDialog && (
        <div className={styles.confirmOverlay} onClick={() => setShowRejectDialog(false)}>
          <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>Reject Timesheet</div>
            <div className={styles.confirmText}>
              Please provide a reason for rejecting this timesheet. The employee will see this
              reason and can make corrections.
            </div>
            <textarea
              className={styles.reasonInput}
              placeholder="Rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500}
            />
            <div className={styles.confirmActions}>
              <Button variant="outline" size="sm" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmReject}
                disabled={rejectTimesheet.isPending || rejectReason.trim().length === 0}
              >
                {rejectTimesheet.isPending ? 'Rejecting...' : 'Confirm Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Full timesheet grid (read-only, with adjacent context + weekly subtotals)
// ---------------------------------------------------------------------------

function TimesheetGrid({ timesheet }: { timesheet: Timesheet }) {
  const [viewingComment, setViewingComment] = useState<{ date: string; comment: string } | null>(null)
  const dates = useMemo(() => {
    const result: string[] = []
    let cur = timesheet.pay_period.start_date
    const end = timesheet.pay_period.end_date
    while (cur <= end) {
      result.push(cur)
      cur = addDays(cur, 1)
    }
    return result
  }, [timesheet])

  const entryByDate = useMemo(() => {
    const map = new Map<string, TimeEntry>()
    timesheet.entries.forEach((e) => map.set(e.date, e))
    return map
  }, [timesheet])

  const { gridRows, weekSubtotals } = useMemo(() => {
    const adjacentContext = timesheet.adjacent_context ?? []
    const prevAdjacent = adjacentContext
      .filter((a) => a.date < timesheet.pay_period.start_date)
      .sort((a, b) => a.date.localeCompare(b.date))
    const nextAdjacent = adjacentContext
      .filter((a) => a.date > timesheet.pay_period.end_date)
      .sort((a, b) => a.date.localeCompare(b.date))

    const rows: GridRow[] = []
    for (const adj of prevAdjacent) {
      rows.push({ date: adj.date, type: 'adjacent', adjacentEntry: adj })
    }
    dates.forEach((d) => {
      rows.push({ date: d, type: 'current', entry: entryByDate.get(d) })
    })
    for (const adj of nextAdjacent) {
      rows.push({ date: adj.date, type: 'adjacent', adjacentEntry: adj })
    }

    // Compute weekly subtotals
    const weekMap = new Map<string, WeekSubtotal>()
    for (const row of rows) {
      const sun = getWeekSunday(row.date)
      if (!weekMap.has(sun)) {
        weekMap.set(sun, { weekSunday: sun, totalHours: 0, standardHours: 0, flsaOvertimeHours: 0, straightOvertimeHours: 0, isIncomplete: false })
      }
      const ws = weekMap.get(sun)!
      if (row.type === 'adjacent' && row.adjacentEntry) {
        const a = row.adjacentEntry
        ws.totalHours += a.hours_worked + a.holiday_hours + a.vacation_hours
        ws.standardHours += a.standard_hours
        ws.flsaOvertimeHours += a.flsa_overtime_hours
        ws.straightOvertimeHours += a.straight_overtime_hours
      } else if (row.type === 'current' && row.entry) {
        ws.totalHours += row.entry.hours
        ws.standardHours += row.entry.standard_hours
        ws.flsaOvertimeHours += row.entry.flsa_overtime_hours
        ws.straightOvertimeHours += row.entry.straight_overtime_hours
      }
    }

    // Mark incomplete weeks
    const periodEnd = timesheet.pay_period.end_date
    for (const ws of weekMap.values()) {
      const weekSaturday = addDays(ws.weekSunday, 6)
      if (weekSaturday > periodEnd) {
        const hasNextAdjacentInWeek = nextAdjacent.some((a) => getWeekSunday(a.date) === ws.weekSunday)
        if (!hasNextAdjacentInWeek) {
          ws.isIncomplete = true
        }
      }
    }

    return { gridRows: rows, weekSubtotals: [...weekMap.values()] }
  }, [timesheet, dates, entryByDate])

  // Compute period totals
  const totals = useMemo(() => {
    let hoursWorked = 0
    let holidayHours = 0
    let vacationHours = 0
    let total = 0

    dates.forEach((d) => {
      const entry = entryByDate.get(d)
      const hw = entry
        ? computeHoursWorked(entry.clock_in_1, entry.clock_out_1, entry.clock_in_2, entry.clock_out_2)
        : 0
      const hh = entry?.holiday_hours ?? 0
      const vh = entry?.vacation_hours ?? 0
      hoursWorked += hw
      holidayHours += hh
      vacationHours += vh
      total += hw + hh + vh
    })

    return { hoursWorked, holidayHours, vacationHours, total }
  }, [dates, entryByDate])

  return (
    <div className={tsStyles.gridScroll}>
      <table className={tsStyles.grid}>
        <thead>
          <tr>
            <th className={tsStyles.gridHeaderCell} style={{ textAlign: 'left' }}>Day</th>
            <th className={tsStyles.gridHeaderCell}>Time In</th>
            <th className={tsStyles.gridHeaderCell}>Time Out</th>
            <th className={tsStyles.gridHeaderCell}>Time In 2</th>
            <th className={tsStyles.gridHeaderCell}>Time Out 2</th>
            <th className={tsStyles.gridHeaderCell}>Hours Worked</th>
            <th className={tsStyles.gridHeaderCell}>Holiday</th>
            <th className={tsStyles.gridHeaderCell}>PTO</th>
            <th className={tsStyles.gridHeaderCell}>Total</th>
            <th className={tsStyles.gridHeaderCell} style={{ width: 36 }}></th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const renderedRows: React.ReactNode[] = []
            let lastWeekSunday: string | null = null

            for (const row of gridRows) {
              const weekSunday = getWeekSunday(row.date)

              // Insert week subtotal before starting a new week
              if (lastWeekSunday !== null && weekSunday !== lastWeekSunday) {
                const subtotal = weekSubtotals.find((ws) => ws.weekSunday === lastWeekSunday)
                if (subtotal) {
                  renderedRows.push(renderWeekSubtotal(lastWeekSunday, subtotal))
                }
              }
              lastWeekSunday = weekSunday

              if (row.type === 'adjacent' && row.adjacentEntry) {
                const adj = row.adjacentEntry
                const adjTotal = adj.hours_worked + adj.holiday_hours + adj.vacation_hours
                renderedRows.push(
                  <tr key={`adj-${row.date}`} className={`${tsStyles.dayRow} ${tsStyles.adjacentRow}`}>
                    <td className={tsStyles.dayLabel}>
                      {formatDateShort(row.date)}
                      <span className={tsStyles.adjacentLabel}>
                        From {formatPeriodShort(adj.pay_period_start, adj.pay_period_end)}
                      </span>
                    </td>
                    <td className={tsStyles.emptyCell} colSpan={4}></td>
                    <td className={`${tsStyles.computedCell} ${tsStyles.readonlyValue}`}>
                      {adj.hours_worked > 0 ? adj.hours_worked.toFixed(2) : ''}
                    </td>
                    <td className={`${tsStyles.computedCell} ${tsStyles.readonlyValue}`}>
                      {adj.holiday_hours > 0 ? adj.holiday_hours.toFixed(0) : ''}
                    </td>
                    <td className={`${tsStyles.computedCell} ${tsStyles.readonlyValue}`}>
                      {adj.vacation_hours > 0 ? adj.vacation_hours.toFixed(0) : ''}
                    </td>
                    <td className={`${tsStyles.computedCell} ${tsStyles.readonlyValue}`}>
                      {adjTotal > 0 ? adjTotal.toFixed(2) : ''}
                    </td>
                    <td className={tsStyles.commentCell ?? tsStyles.emptyCell}>
                      {adj.comment && (
                        <button
                          type="button"
                          className={tsStyles.commentIconFilled}
                          onClick={() => setViewingComment({ date: row.date, comment: adj.comment! })}
                        >
                          💬
                        </button>
                      )}
                    </td>
                  </tr>,
                )
              } else if (row.type === 'current') {
                const entry = row.entry
                const weekend = isOffDay(row.date, timesheet.work_days)
                const hw = entry
                  ? computeHoursWorked(entry.clock_in_1, entry.clock_out_1, entry.clock_in_2, entry.clock_out_2)
                  : 0
                const holidayHrs = entry?.holiday_hours ?? 0
                const vacHrs = entry?.vacation_hours ?? 0
                const dayTotal = hw + holidayHrs + vacHrs

                renderedRows.push(
                  <tr key={row.date} className={`${tsStyles.dayRow} ${weekend ? tsStyles.weekendRow : ''} ${vacHrs > 0 ? tsStyles.ptoRow : ''}`}>
                    <td className={tsStyles.dayLabel}>{formatDateShort(row.date)}</td>
                    <td className={tsStyles.computedCell}>{formatTime(entry?.clock_in_1 ?? null)}</td>
                    <td className={tsStyles.computedCell}>{formatTime(entry?.clock_out_1 ?? null)}</td>
                    <td className={tsStyles.computedCell}>{formatTime(entry?.clock_in_2 ?? null)}</td>
                    <td className={tsStyles.computedCell}>{formatTime(entry?.clock_out_2 ?? null)}</td>
                    <td className={tsStyles.computedCell}>{hw > 0 ? hw.toFixed(2) : ''}</td>
                    <td className={`${tsStyles.computedCell} ${tsStyles.readonlyValue}`}>
                      {holidayHrs > 0 ? holidayHrs : ''}
                    </td>
                    <td className={tsStyles.computedCell}>{vacHrs > 0 ? vacHrs : ''}</td>
                    <td className={tsStyles.computedCell}>
                      <strong>{dayTotal > 0 ? dayTotal.toFixed(2) : ''}</strong>
                    </td>
                    <td className={tsStyles.commentCell ?? tsStyles.emptyCell}>
                      {entry?.comment && (
                        <button
                          type="button"
                          className={tsStyles.commentIconFilled}
                          onClick={() => setViewingComment({ date: row.date, comment: entry!.comment! })}
                        >
                          💬
                        </button>
                      )}
                    </td>
                  </tr>,
                )
              }
            }

            // Final week subtotal
            if (lastWeekSunday !== null) {
              const subtotal = weekSubtotals.find((ws) => ws.weekSunday === lastWeekSunday)
              if (subtotal) {
                renderedRows.push(renderWeekSubtotal(lastWeekSunday, subtotal))
              }
            }

            return renderedRows
          })()}

          {/* Period totals */}
          <tr className={tsStyles.totalsRow}>
            <td className={tsStyles.totalLabel}>Period Total</td>
            <td className={tsStyles.totalValue}></td>
            <td className={tsStyles.totalValue}></td>
            <td className={tsStyles.totalValue}></td>
            <td className={tsStyles.totalValue}></td>
            <td className={tsStyles.totalValue}>{totals.hoursWorked.toFixed(2)}</td>
            <td className={tsStyles.totalValue}>
              {totals.holidayHours > 0 ? totals.holidayHours.toFixed(0) : '0'}
            </td>
            <td className={tsStyles.totalValue}>{totals.vacationHours.toFixed(0)}</td>
            <td className={tsStyles.totalValue}>
              <strong>{totals.total.toFixed(2)}</strong>
            </td>
            <td className={tsStyles.totalValue}></td>
          </tr>
        </tbody>
      </table>

      {/* Comment view popover */}
      {viewingComment && (
        <div className={styles.confirmOverlay} onClick={() => setViewingComment(null)}>
          <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>Comment</div>
            <div style={{ marginBottom: 8, fontSize: '0.85rem', color: colors.gray70 }}>
              {formatDateShort(viewingComment.date)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', marginBottom: 16 }}>
              {viewingComment.comment}
            </div>
            <div className={styles.confirmActions}>
              <Button variant="outline" size="sm" onClick={() => setViewingComment(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function renderWeekSubtotal(weekSunday: string, subtotal: WeekSubtotal) {
  return (
    <tr key={`ws-${weekSunday}`} className={tsStyles.weekSubtotalRow}>
      <td className={tsStyles.weekSubtotalLabel}>
        Week Total
        {subtotal.isIncomplete && (
          <span style={{ color: colors.gray50, fontWeight: 'normal', marginLeft: 4, fontSize: '0.75rem' }}>
            (partial week)
          </span>
        )}
      </td>
      <td className={tsStyles.weekSubtotalValue} colSpan={4}></td>
      <td className={tsStyles.weekSubtotalValue}></td>
      <td className={tsStyles.weekSubtotalValue}></td>
      <td className={tsStyles.weekSubtotalValue}></td>
      <td className={tsStyles.weekSubtotalValue}></td>
      <td className={tsStyles.weekSubtotalValue}>
        {subtotal.totalHours > 0 ? subtotal.totalHours.toFixed(2) : ''}
        {subtotal.isIncomplete ? (
          <span className={tsStyles.otIndicator} title="OT pending — week spans into next pay period">
            <span className={tsStyles.otDotPending} />
          </span>
        ) : (
          <>
            {subtotal.flsaOvertimeHours > 0 && (
              <span className={tsStyles.otIndicator} title={`${subtotal.flsaOvertimeHours.toFixed(2)} hrs OT (1.5x)`}>
                <span className={tsStyles.otDotFlsa} />
                <span className={tsStyles.otValueFlsa}>{subtotal.flsaOvertimeHours.toFixed(2)}</span>
              </span>
            )}
            {subtotal.straightOvertimeHours > 0 && (
              <span className={tsStyles.otIndicator} title={`${subtotal.straightOvertimeHours.toFixed(2)} hrs Straight OT (1.0x)`}>
                <span className={tsStyles.otDotStraight} />
                <span className={tsStyles.otValueStraight}>{subtotal.straightOvertimeHours.toFixed(2)}</span>
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  )
}
